import React from 'react';

interface ConicGlowCardProps {
  children: React.ReactNode;
  className?: string;
  glowIntensity?: number; // 0 to 1
  active?: boolean;
  glowVariant?: 'gemini' | 'emerald' | 'gold';
  onClick?: (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchEnd?: (e: React.TouchEvent<HTMLDivElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  ariaLabel?: string;
  style?: React.CSSProperties;
}

export const ConicGlowCard: React.FC<ConicGlowCardProps> = ({
  children,
  className = '',
  glowIntensity = 0.5,
  active = false,
  glowVariant = 'gemini',
  onClick,
  onMouseEnter,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
  onFocus,
  onBlur,
  ariaLabel,
  style
}) => {
  const isClickable = typeof onClick === 'function';

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onClick) return;
    const target = e.target as HTMLElement | null;
    if (target && target !== e.currentTarget) {
      const nestedInteractive = target.closest('button, a, input, select, textarea');
      if (
        nestedInteractive &&
        nestedInteractive !== e.currentTarget &&
        !nestedInteractive.classList.contains('venue-card-btn')
      ) {
        return;
      }
    }
    onClick(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <div
      className={`conic-glow-wrapper ${active ? 'active-glow' : ''} ${glowVariant !== 'gemini' ? `glow-variant-${glowVariant}` : ''} ${isClickable ? 'clickable-card' : ''} ${className}`.trim()}
      style={{
        '--glow-opacity': `${Math.max(0.2, Math.min(1.0, glowIntensity))}`,
        cursor: isClickable ? 'pointer' : undefined,
        ...style
      } as React.CSSProperties}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onFocus={onFocus}
      onBlur={onBlur}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={ariaLabel}
    >
      <div className="conic-glow-card">
        {children}
      </div>
    </div>
  );
};

